/* global navigator window document */
import { useEffect, useState, useCallback } from 'react'

function isStandaloneNow() {
  if (typeof navigator === 'undefined') return false
  return (
    navigator.standalone === true ||
    (typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  )
}

function isIOSDevice() {
  if (typeof navigator === 'undefined') return false
  return /iPhone|iPad|iPod/.test(navigator.userAgent)
}

// iOS Safari never fires beforeinstallprompt and ignores manifest.json for
// install purposes - the only path there is a manual Share -> Add to Home
// Screen, so the UI needs a separate instructional banner instead of a
// native install button. See PWA-NOTES.md.
export function useInstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null)
  const [isStandalone, setIsStandalone] = useState(isStandaloneNow)

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault()
      setDeferredEvent(event)
    }
    const handleAppInstalled = () => {
      setDeferredEvent(null)
      setIsStandalone(true)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  // Browsers only honor event.prompt() when called from a real user gesture
  // (e.g. a button's onClick), never automatically on mount.
  const promptInstall = useCallback(() => {
    if (!deferredEvent) return Promise.resolve(null)
    deferredEvent.prompt()
    return deferredEvent.userChoice.finally(() => setDeferredEvent(null))
  }, [deferredEvent])

  const isIOSInstallable = isIOSDevice() && !isStandalone

  return {
    isStandalone,
    isIOSInstallable,
    canPromptInstall: !!deferredEvent,
    promptInstall,
  }
}
